import { storage } from "@flatfile/shared-ui";
import constate from "constate";
import { useState, useMemo, useEffect, useCallback } from "react";
import { usePinColumns } from "../hooks/usePinColumns.js";
import { useTheme } from "styled-components";
const MIN_COLUMN_WIDTH = 100;
const getResizedColumnId = (tableId, colIndex) => {
  return `${tableId}-${colIndex}`;
};
const getColumnWidth = (column, theme, userResizedWidths, tableId, colIndex) => {
  const columnIndex = column.index ?? colIndex;
  const stored = userResizedWidths == null ? void 0 : userResizedWidths[getResizedColumnId(tableId, columnIndex)];
  if (!column)
    return theme.indexColumn.width;
  if (stored !== void 0)
    return stored;
  if (column && (column.width || column.width === 0))
    return column.width;
  if (theme.column.width < MIN_COLUMN_WIDTH)
    return MIN_COLUMN_WIDTH;
  return theme.column.width;
};
const getColumnHeaderStyles = (theme, column, columnDiff) => {
  var _a;
  const highlightAutoGenerated = (_a = column.treatments) == null ? void 0 : _a.includes("ai-defined");
  const changeType = columnDiff == null ? void 0 : columnDiff[column.value];
  let headerTextColor = theme.column.header.color;
  let headerBGColor = theme.column.header.backgroundColor;
  let headerTextDecoration = "none";
  if (highlightAutoGenerated) {
    headerTextColor = theme.column.header.autoGeneratedColor;
    headerBGColor = theme.column.header.autoGeneratedBackgroundColor;
  }
  if (changeType === "added") {
    headerTextColor = theme.column.header.addedColor;
    headerBGColor = theme.column.header.addedBackgroundColor;
  }
  if (changeType === "changed") {
    headerTextColor = theme.column.header.changedColor;
    headerBGColor = theme.column.header.changedBackgroundColor;
  }
  if (changeType === "removed") {
    headerTextColor = theme.column.header.removedColor;
    headerTextDecoration = "line-through";
  }
  return {
    color: headerTextColor,
    background: headerBGColor,
    textDecoration: headerTextDecoration
  };
};
const getColumnStyles = (columnDiff, columnConfig, pinnedColumns = [], theme, userResizedWidths, tableId, isDiffView) => {
  const styles = [];
  let left = theme.indexColumn.width;
  pinnedColumns.map((columnIndex) => {
    const column = columnConfig[columnIndex];
    const width = column ? getColumnWidth(column, theme, userResizedWidths, tableId, columnIndex) : 0;
    styles.push({ width, left });
    left += width;
  });
  columnConfig.map((column, index) => {
    if (!pinnedColumns.includes(index)) {
      const width = getColumnWidth(
        column,
        theme,
        userResizedWidths,
        tableId,
        index
      );
      const headerStyles = getColumnHeaderStyles(theme, column, columnDiff);
      styles.push({
        width,
        left,
        header: headerStyles
      });
      left += width;
    }
  });
  return styles;
};
const getColumnClassNames = (columnConfig, isDiffView) => {
  const classNames = [];
  columnConfig.map((column, index) => {
    var _a;
    const isAiGenerated = (_a = column.treatments) == null ? void 0 : _a.includes("ai-defined");
    classNames.push(isAiGenerated && !isDiffView ? "cell-ai-generated" : "");
  });
  return classNames;
};
const useColumnsContext = ({
  columnDiff,
  columnConfig,
  disableColumnMenus,
  tableId,
  isDiffView
}) => {
  const theme = useTheme();
  const { pinnedColumns, addOrRemovePinnedColumns, setPinnedColumns } = usePinColumns();
  const resizedColumnsId = `${tableId}-resizeColumnWidths`;
  const store = storage(resizedColumnsId);
  const [userResizedWidths, setUserResizedWidths] = useState(
    () => store.exists() && store.getParsed() || {}
  );
  const pinnedColumnsMap = useMemo(() => {
    if (!pinnedColumns)
      return {};
    return pinnedColumns.reduce(
      (memo, pinnedColumnIndex) => {
        memo[pinnedColumnIndex] = columnConfig[pinnedColumnIndex].value;
        return memo;
      },
      {}
    );
  }, [pinnedColumns]);
  useEffect(() => {
    const newPinnedColumns = pinnedColumns.reduce(
      (memo, pinnedColumnIndex) => {
        const value = pinnedColumnsMap[pinnedColumnIndex];
        const newIndex = columnConfig.findIndex((c) => c.value === value);
        if (newIndex > -1) {
          memo.push(newIndex);
        }
        return memo;
      },
      []
    );
    setPinnedColumns(newPinnedColumns);
  }, [columnConfig]);
  useEffect(() => {
    if (tableId) {
      store.set(userResizedWidths);
    }
  }, [tableId, userResizedWidths]);
  const columnStyles = useMemo(
    () => getColumnStyles(
      columnDiff ?? {},
      columnConfig,
      pinnedColumns,
      theme,
      userResizedWidths,
      tableId
    ),
    [
      tableId,
      userResizedWidths,
      pinnedColumns,
      columnConfig,
      theme.indexColumn.width,
      columnDiff,
      isDiffView
    ]
  );
  const setColumnWidth = useCallback(
    (id, width) => {
      setUserResizedWidths((widths) => ({ ...widths, [id]: width }));
    },
    [setUserResizedWidths]
  );
  const renderedArray = useMemo(
    () => Array(columnConfig.length).fill(null).map((x, i) => i),
    [columnConfig]
  );
  const absoluteArray = useMemo(() => {
    const filteredAbsoluteArray = renderedArray.filter(
      (x) => !pinnedColumns.includes(x)
    );
    return [...pinnedColumns, ...filteredAbsoluteArray];
  }, [pinnedColumns, renderedArray]);
  const [columnIndexToValue, columnValueToIndex] = useMemo(() => {
    const keyToAbsolute = {};
    const absoluteToKey = {};
    absoluteArray.forEach((configIndex, absoluteIndex) => {
      const columnKey = columnConfig[configIndex].value;
      keyToAbsolute[columnKey] = absoluteIndex;
      absoluteToKey[absoluteIndex] = columnKey;
    });
    return [absoluteToKey, keyToAbsolute];
  }, [absoluteArray, columnConfig]);
  const getAbsoluteColumnIndex = useCallback(
    (columnIndex) => {
      return columnIndex < 0 ? columnIndex : absoluteArray[columnIndex];
    },
    [pinnedColumns, absoluteArray]
  );
  const columnClassNames = useMemo(
    () => getColumnClassNames(columnConfig, isDiffView),
    [columnConfig, isDiffView]
  );
  return {
    columnIndexToValue,
    columnValueToIndex,
    columnDiff: columnDiff ?? {},
    columnConfig,
    columnStyles,
    columnClassNames,
    getAbsoluteColumnIndex,
    setColumnWidth,
    pinnedColumns,
    addOrRemovePinnedColumns,
    disableColumnMenus
  };
};
const [ColumnsProvider, useColumns] = constate(useColumnsContext);
export {
  ColumnsProvider,
  MIN_COLUMN_WIDTH,
  getResizedColumnId,
  useColumns
};
